import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
    children: ReactNode;
    fallback?: ReactNode;
}

interface State {
    hasError: boolean;
    error: Error | null;
    errorInfo: ErrorInfo | null;
}

const isChunkLoadError = (error: Error): boolean => {
    const msg = error?.message || '';
    return (
        msg.includes('Failed to fetch dynamically imported module') ||
        msg.includes('Importing a module script failed') ||
        msg.includes('dynamically imported module') ||
        error?.name === 'ChunkLoadError'
    );
};

export class ErrorBoundary extends Component<Props, State> {
    private reloadAttempted = false;

    public state: State = {
        hasError: false,
        error: null,
        errorInfo: null
    };

    public static getDerivedStateFromError(error: Error): State {
        return { hasError: true, error, errorInfo: null };
    }

    public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        console.error('Uncaught error:', error, errorInfo);
        this.setState({ errorInfo });

        // Chunk load errors happen when a new build is deployed while the app is open.
        // The service worker has cached the old chunk hashes; the new index.js references
        // new hashes that don't exist in the cache yet. A hard reload forces the SW to
        // fetch the new manifest and cache the new chunks.
        if (isChunkLoadError(error) && !this.reloadAttempted) {
            this.reloadAttempted = true;
            console.warn('[ErrorBoundary] Chunk load error detected — reloading to pick up new build...');
            window.location.reload();
        }
    }

    public render() {
        if (this.state.hasError) {
            // If it's a chunk error we're already reloading — show a neutral message
            if (this.state.error && isChunkLoadError(this.state.error)) {
                return (
                    <div className="flex h-screen items-center justify-center flex-col gap-3 text-gray-500">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" />
                        <p className="text-sm">Mise à jour détectée, rechargement...</p>
                    </div>
                );
            }

            return (
                <div className="p-4 bg-red-50 border border-red-200 rounded-md m-4">
                    <h2 className="text-lg font-semibold text-red-800 mb-2">Something went wrong</h2>
                    <details className="whitespace-pre-wrap text-sm text-red-700">
                        <summary>Détails</summary>
                        {this.state.error && this.state.error.toString()}
                        <br />
                        {this.state.errorInfo && this.state.errorInfo.componentStack}
                    </details>
                    <button
                        onClick={() => window.location.reload()}
                        className="mt-3 px-4 py-2 bg-red-600 text-white rounded text-sm hover:bg-red-700"
                    >
                        Recharger la page
                    </button>
                </div>
            );
        }

        return this.props.children;
    }
}
